import { useModalManager } from '../../hooks/ModalManager.hook';
import { useEffect, useState } from 'react';
import { Tag } from '../../redux/ControlPanel/TagControl/TagControl.type';
import styled, { css } from 'styled-components';
import CrossIcon from '../Icons/Cross.icon';
import { useTranslation } from 'react-i18next';
import TagsEditModal from '../Modal/Tag/TagsEditModal';
import useAuthHook from '../../hooks/useAuth.hook';
import { checkUserRequiredPermissions } from '../../utils/getUserRolesByToken';
import { UserPermissions } from '../../types/userPermissions.types';

export interface TagBlockProps {
  label?: string;
  value?: Tag[];
  onTagsChange?: (items: Tag[]) => void;
  maxCount?: number;
}

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StyledTagWrapper = styled.div`
  display: flex;
  gap: 4px;
  max-width: 100%;
  flex-wrap: wrap;
`;

export const StyledInputLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 22px;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 6px;
`;

const StyledTag = styled.span<{ isAdding?: boolean }>`
  white-space: nowrap;
  padding: 3px 5px;
  font-size: 12px;
  background-color: ${(props: any) =>
    props?.isAdding ? '#F8FCFF' : '#FFFEF2'};
  border: 1px solid ${props => (props?.isAdding ? '#EEE' : '#FEF1B1')};
  color: #404851;
  border-radius: 3px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  user-select: none;
  cursor: ${props => (props?.isAdding ? 'pointer' : 'default')};
  ${props =>
    props.isAdding &&
    css`
      &:hover {
        border-color: #999;
      }
    `}
`;

const StyledActionWrapper = styled.span`
  cursor: pointer;
`;

const TagBlock = ({
  label,
  value = [],
  onTagsChange,
  maxCount = 10,
}: TagBlockProps) => {
  const { openModal } = useModalManager();
  const { user } = useAuthHook();

  const [tags, setTags] = useState<Tag[]>([]);
  const { t } = useTranslation();
  useEffect(() => {
    setTags(value || []);
  }, [value]);

  const handleRemoveTag = (tag: Tag) => {
    const res = tags.filter(t => t.id !== tag.id);
    setTags(res);
    onTagsChange?.(res);
  };

  const handleChangeTag = (tags: Tag[]) => {
    setTags(tags);
    onTagsChange?.(tags);
  };

  const handleAddTag = () => {
    openModal(TagsEditModal, {
      initialValues: tags,
      maxCount,
      onSave: handleChangeTag,
    });
  };

  return (
    <StyledWrapper>
      {label && <StyledInputLabel>{label}</StyledInputLabel>}
      <StyledTagWrapper>
        {tags.map(tag => (
          <StyledTag key={tag.id}>
            {tag.name}
            {onTagsChange &&
              checkUserRequiredPermissions(user.permissions, [
                UserPermissions.CAN_DELETE_TAG,
              ]) && (
                <StyledActionWrapper onClick={() => handleRemoveTag(tag)}>
                  <CrossIcon width={10} height={10} />
                </StyledActionWrapper>
              )}
          </StyledTag>
        ))}
        {onTagsChange &&
          tags?.length < maxCount &&
          checkUserRequiredPermissions(user.permissions, [
            UserPermissions.CAN_CREATE_TAG,
          ]) && (
            <StyledTag key={0} isAdding={true} onClick={handleAddTag}>
              {t('Add tag')}
            </StyledTag>
          )}
      </StyledTagWrapper>
    </StyledWrapper>
  );
};

export default TagBlock;
